import React from 'react';
import { Calendar, Hash, Clock, Ban } from 'lucide-react';

const QRLimitsPanel = ({ limits, onLimitsChange }) => {
  const handleExpiryToggle = () => {
    onLimitsChange?.({
      ...limits,
      hasExpiry: !limits?.hasExpiry,
      expiryDate: !limits?.hasExpiry ? getDefaultExpiryDate() : null
    });
  };

  const handleScanLimitToggle = () => {
    onLimitsChange?.({
      ...limits,
      hasMaxScans: !limits?.hasMaxScans,
      maxScans: !limits?.hasMaxScans ? 100 : null
    });
  };

  const getDefaultExpiryDate = () => {
    const date = new Date();
    date?.setDate(date?.getDate() + 30); // Default to 30 days from now
    return date?.toISOString()?.split('T')?.[0];
  };

  const formatDateForInput = (isoDate) => {
    if (!isoDate) return '';
    return isoDate?.split('T')?.[0];
  };

  const handleDateChange = (e) => {
    const dateValue = e?.target?.value;
    if (dateValue) {
      // Convert to ISO timestamp for database
      const date = new Date(dateValue + 'T23:59:59');
      onLimitsChange?.({
        ...limits,
        expiryDate: date?.toISOString()
      });
    }
  };

  const getExpiryStatus = () => {
    if (!limits?.hasExpiry || !limits?.expiryDate) return null;
    
    const expiryDate = new Date(limits.expiryDate);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { type: 'expired', message: 'Expired', icon: Ban, color: 'text-red-600' };
    } else if (daysUntilExpiry === 0) {
      return { type: 'today', message: 'Expires today', icon: Clock, color: 'text-orange-600' };
    } else if (daysUntilExpiry <= 3) {
      return { type: 'soon', message: `Expires in ${daysUntilExpiry} days`, icon: Clock, color: 'text-orange-600' };
    } else {
      return { type: 'active', message: `Expires in ${daysUntilExpiry} days`, icon: Calendar, color: 'text-green-600' };
    }
  };

  const getScanLimitStatus = () => {
    if (!limits?.hasMaxScans || !limits?.maxScans) return null;
    
    const remaining = limits?.maxScans - (limits?.currentScans || 0);
    
    if (remaining <= 0) {
      return { type: 'reached', message: 'Limit reached', icon: Ban, color: 'text-red-600' };
    } else if (remaining <= limits?.maxScans * 0.1) { // 10% remaining
      return { type: 'low', message: `${remaining} scans left`, icon: Hash, color: 'text-orange-600' };
    } else {
      return { type: 'active', message: `${remaining} scans remaining`, icon: Hash, color: 'text-green-600' };
    }
  };

  const expiryStatus = getExpiryStatus();
  const scanStatus = getScanLimitStatus();

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">QR Code Limits</h3>
      <div className="space-y-6">
        {/* Expiry Date Section */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gray-600" />
              <label className="text-sm font-medium text-gray-700">
                Set Expiry Date
              </label>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={limits?.hasExpiry || false}
                onChange={handleExpiryToggle}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          
          {limits?.hasExpiry && (
            <div className="space-y-3">
              <div>
                <input
                  type="date"
                  value={formatDateForInput(limits?.expiryDate)}
                  onChange={handleDateChange}
                  min={new Date()?.toISOString()?.split('T')?.[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {expiryStatus && (
                <div className={`flex items-center gap-2 text-sm ${expiryStatus?.color}`}>
                  <expiryStatus.icon size={16} />
                  <span>{expiryStatus?.message}</span>
                </div>
              )}
              
              <p className="text-xs text-gray-500">
                QR code will become inactive after this date
              </p>
            </div>
          )}
        </div>

        {/* Scan Limit Section */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Hash className="w-5 h-5 text-gray-600" />
              <label className="text-sm font-medium text-gray-700">
                Set Scan Limit
              </label>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={limits?.hasMaxScans || false}
                onChange={handleScanLimitToggle}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          
          {limits?.hasMaxScans && (
            <div className="space-y-3">
              <div>
                <input
                  type="number"
                  value={limits?.maxScans || ''}
                  onChange={(e) => onLimitsChange?.({
                    ...limits,
                    maxScans: parseInt(e?.target?.value) || null
                  })}
                  min="1"
                  max="999999"
                  placeholder="100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {scanStatus && (
                <div className={`flex items-center gap-2 text-sm ${scanStatus?.color}`}>
                  <scanStatus.icon size={16} />
                  <span>{scanStatus?.message}</span>
                </div>
              )}
              
              <p className="text-xs text-gray-500">
                QR code will become inactive after reaching this number of scans
              </p>
            </div>
          )}
        </div>

        {/* Quick Presets */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Quick Presets</h4>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => onLimitsChange?.({
                hasExpiry: true,
                expiryDate: new Date(Date.now() + 24 * 60 * 60 * 1000)?.toISOString(),
                hasMaxScans: false,
                maxScans: null
              })}
              className="px-3 py-2 text-xs bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors"
            >
              24 Hours
            </button>
            <button
              onClick={() => onLimitsChange?.({
                hasExpiry: true,
                expiryDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)?.toISOString(),
                hasMaxScans: false,
                maxScans: null
              })}
              className="px-3 py-2 text-xs bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors"
            >
              1 Week
            </button>
            <button
              onClick={() => onLimitsChange?.({
                hasExpiry: false,
                expiryDate: null,
                hasMaxScans: true,
                maxScans: 100
              })}
              className="px-3 py-2 text-xs bg-green-50 text-green-700 rounded-md hover:bg-green-100 transition-colors"
            >
              100 Scans
            </button>
            <button
              onClick={() => onLimitsChange?.({
                hasExpiry: false,
                expiryDate: null,
                hasMaxScans: true,
                maxScans: 1000
              })}
              className="px-3 py-2 text-xs bg-green-50 text-green-700 rounded-md hover:bg-green-100 transition-colors"
            >
              1000 Scans
            </button>
          </div>
        </div>

        {/* Summary */}
        {(limits?.hasExpiry || limits?.hasMaxScans) && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Active Limits:</h4>
            <div className="space-y-1 text-sm text-gray-600">
              {limits?.hasExpiry && limits?.expiryDate && (
                <div className="flex items-center gap-2">
                  <Calendar size={14} />
                  <span>Expires: {new Date(limits.expiryDate)?.toLocaleDateString()}</span>
                </div>
              )}
              {limits?.hasMaxScans && limits?.maxScans && (
                <div className="flex items-center gap-2">
                  <Hash size={14} />
                  <span>Max scans: {limits?.maxScans}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QRLimitsPanel;